import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { trackEvent } from '../../../../shared/utils/analytics';
import './InviteCodeModal.css';

function InviteCodeModal({ isOpen, onClose }) {
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setInviteCode('');
      setError('');
      setCopied(false);
      return;
    }
    setIsLoading(true);
    setError('');
    fetch('/api/user.php', { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load invite code');
        return res.json();
      })
      .then((data) => {
        setInviteCode(data.invite_code ?? '');
        if (!(data.invite_code ?? '')) {
          setError('Invite code not available.');
        }
      })
      .catch((err) => {
        console.error(err);
        setError('Failed to load your invite code.');
      })
      .finally(() => setIsLoading(false));
  }, [isOpen]);

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const inviteLink = inviteCode ? `https://picotodo.com/?invite=${encodeURIComponent(inviteCode)}` : '';

  const handleCopy = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink).then(() => {
      setCopied(true);
      trackEvent('invite_link_copied');
    });
  };

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="invite-code-modal-backdrop" onClick={handleBackdropClick}>
      <div className="invite-code-modal">
        <button
          type="button"
          className="invite-code-modal-close text-non-selectable"
          onClick={onClose}
          aria-label="Close"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4 L4 12 M4 4 L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>

        <h2>Your invite code</h2>
        <p className="invite-code-modal-subtitle">
          Share this link with others so they can sign up. Anyone with a valid invite code can create an account.
        </p>

        {isLoading ? (
          <div className="invite-code-modal-loading">Loading…</div>
        ) : error ? (
          <div className="invite-code-modal-error">{error}</div>
        ) : inviteLink ? (
          <div className="invite-code-modal-code-row">
            <code className="invite-code-modal-code invite-code-modal-link">{inviteLink}</code>
            <button
              type="button"
              className="invite-code-modal-copy text-non-selectable"
              onClick={handleCopy}
            >
              {copied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default InviteCodeModal;
